import { Stack, Group, Text, Tooltip, ThemeIcon, UnstyledButton } from '@mantine/core';
import { Star } from 'react-feather';
import {
  ENGAGEMENT_RATING_CONFIG,
  ENGAGEMENT_RATING_MAP,
} from '../../../config/engagementRatingConfig';

interface GroupEngagementRatingProps {
  rating: number | null;
  onRatingChange: (rating: number) => void;
}

export function GroupEngagementRating({
  rating,
  onRatingChange,
}: GroupEngagementRatingProps) {
  return (
    <Stack gap="sm">
      <Group gap="xs">
        <ThemeIcon size="sm" variant="light" color="yellow">
          <Star size={14} />
        </ThemeIcon>
        <Text fw={600} size="sm">
          Group Engagement Rating
        </Text>
      </Group>
      <Text size="xs" c="dimmed">
        Rate the overall group engagement during this session (1-5)
      </Text>

      <Group gap={6}>
        {ENGAGEMENT_RATING_CONFIG.map((config) => {
          const isSelected = rating === config.value;
          const ratingInfo = ENGAGEMENT_RATING_MAP.get(config.value);
          return (
            <Tooltip
              key={config.value}
              label={`${ratingInfo?.label} — ${ratingInfo?.description}`}
              position="top"
              withArrow
            >
              <UnstyledButton
                onClick={() => onRatingChange(config.value)}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 'var(--mantine-radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 15,
                  border: isSelected
                    ? `2px solid var(--mantine-color-${config.color}-6)`
                    : '1px solid var(--mantine-color-gray-3)',
                  backgroundColor: isSelected
                    ? `var(--mantine-color-${config.color}-1)`
                    : 'var(--mantine-color-white)',
                  color: isSelected
                    ? `var(--mantine-color-${config.color}-8)`
                    : 'var(--mantine-color-gray-6)',
                  transition: 'all 150ms ease',
                }}
              >
                {config.value}
              </UnstyledButton>
            </Tooltip>
          );
        })}
        {rating != null && (
          <Text size="sm" c="dimmed" ml="xs">
            {ENGAGEMENT_RATING_MAP.get(rating)?.label}
          </Text>
        )}
      </Group>
    </Stack>
  );
}

interface ParticipantEngagementRatingProps {
  rating: number | null;
  onRatingChange: (rating: number) => void;
  readOnly?: boolean;
}

export function ParticipantEngagementRating({
  rating,
  onRatingChange,
  readOnly = false,
}: ParticipantEngagementRatingProps) {
  if (readOnly) {
    const ratingInfo = rating != null ? ENGAGEMENT_RATING_MAP.get(rating) : null;
    return (
      <Group gap={6} align="center" className="med-participant-engagement">
        <Text size="xs" c="dimmed" fw={500}>
          Engagement Level
        </Text>
        {rating != null ? (
          <Group gap={4}>
            <Text
              size="xs"
              fw={600}
              c={ratingInfo ? `${ratingInfo.color}.7` : undefined}
            >
              {rating}
            </Text>
            <Text size="xs" c="dimmed">
              {ratingInfo?.label}
            </Text>
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Group>
    );
  }

  return (
    <Group gap={6} align="center" className="med-participant-engagement">
      <Text size="xs" c="dimmed" fw={500}>
        Engagement Level
      </Text>
      <Group gap={6} align="center" wrap="nowrap" className="med-participant-engagement-buttons">
        {ENGAGEMENT_RATING_CONFIG.map((config) => {
          const isSelected = rating === config.value;
          const ratingInfo = ENGAGEMENT_RATING_MAP.get(config.value);
          return (
            <Tooltip
              key={config.value}
              label={`${ratingInfo?.label} — ${ratingInfo?.description}`}
              position="top"
              withArrow
            >
              <UnstyledButton
                onClick={() => onRatingChange(config.value)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 'var(--mantine-radius-sm)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 12,
                  border: isSelected
                    ? `2px solid var(--mantine-color-${config.color}-6)`
                    : '1px solid var(--mantine-color-gray-3)',
                  backgroundColor: isSelected
                    ? `var(--mantine-color-${config.color}-1)`
                    : 'var(--mantine-color-white)',
                  color: isSelected
                    ? `var(--mantine-color-${config.color}-8)`
                    : 'var(--mantine-color-gray-6)',
                  transition: 'all 150ms ease',
                }}
              >
                {config.value}
              </UnstyledButton>
            </Tooltip>
          );
        })}
        {rating != null && (
          <Text size="xs" c="dimmed" ml={4}>
            {ENGAGEMENT_RATING_MAP.get(rating)?.label}
          </Text>
        )}
      </Group>
    </Group>
  );
}

export default GroupEngagementRating;
